import {
  WhatsAppMessageBase,
  WhatsAppChat,
  WhatsAppMessageResponse,
} from "./whatsapp";
import { SMSMessageResponse } from "./sms";
import { AudioFile } from "./audioFile"; // Assuming AudioFile will be in audioFile.ts
import { PartnerAddress, PartnerResponse } from "./partner";
import { ProductResponse } from "./product";
import { UserResponse } from "./user";
import { UnitResponse } from "./unit";
import { Email } from "./email";
import { Employee } from "./employee";
import type { LocationResponse } from "./location";

// Lightweight email summary attached to a pre-order via pre_order_emails.
// Emitted by the recap endpoint AND the offline shim — only the fields the UI
// needs to render a picker and resolve per-user visibility. Full Email is
// fetched on demand. `user_id` + `message_id` drive sibling-email collapsing
// in useLinkedSources (group same Message-Id rows, prefer the entry owned by
// the current user).
export interface PreOrderLinkedEmail {
  id: string;
  email_subject?: string;
  from_address?: string;
  email_date?: string;
  email_type?: number | null;
  user_id?: string;
  message_id?: string;
}

// Custom entity for transport mode (partner=sender, product=destination not in DB)
export interface CustomEntity {
  name: string;
  address_1?: string | null;
  address_2?: string | null;
  code_postal?: string | null;
  ville?: string | null;
  pays?: string | null;
}

// PreOrder Source Type enum - matches stkdb PreOrderSourceType
export enum PreOrderSourceType {
  EMAIL = 0,
  WHATSAPP = 1,
  MANUAL = 3,
  INTERNAL_OFFER = 4,
  SMS = 5,
  ERP = 6,
  AI_CHAT = 7,
}

// PreOrder Type enum - matches stkdb PreOrderType
export enum PreOrderType {
  PRE_ORDER = 0,
  AVOIR = 1,
  PURCHASE = 2,
}

/**
 * Business category — distinguishes customer-facing sales from supplier-facing
 * purchases. Independent of `PreOrderType` (the document type). Legacy
 * `/purchases` filtered purely on category; some purchase rows ship with
 * `type=PRE_ORDER` and only `category=PURCHASES` tells them apart.
 */
export enum PreOrderCategory {
  SALES = 0,
  PURCHASES = 1,
}

// PreOrder Status enum - matches stkdb PreOrderStatus
// (libs/stkdb/src/stkdb/models/pre_orders.py)
export enum PreOrderStatus {
  PENDING = 0,
  CONFIRMED = 1,
  EXPORTED = 2,
  RESERVED = 3,
  SENT = 4,
  PAID = 5,
  CANCELLED = 6,
  EXPORT_FAILED = 7,
  /** Whole pre-order is an AI proposal awaiting user accept/decline. */
  AI_PROPOSAL = 8,
}

// PreOrder Flow types - these replace the old content field
// Using backend field names for consistency
export interface PreOrderFlow {
  id: string;
  pre_order_id: string;
  product_id?: string | null;
  partner_id?: string | null;
  product_code?: string | null;
  quantity?: number | null; // quantite_unite (units)
  weight?: number | null; // quantite_poids (kg)
  unit_id?: string | null; // unit UUID
  unit?: UnitResponse | null; // unit object
  price_per_weight?: number | null; // price (per kg)
  comment?: string | null; // visible comment (for client/delivery)
  hidden_comment?: string | null; // hidden comment (internal, for preparation)
  status: number; // 0: pending, 1: confirmed, 2: flagged, 3: cancelled
  index?: number | null; // Order of the flow in the pre-order
  delivery_date?: string | null;
  order_date?: string | null;
  order_id?: string | null;
  cancelled_at?: string | null;
  confirmed_at?: string | null;
  cancellation_reason?: string | null;
  flow_metadata?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null; // Soft delete timestamp
  product?: ProductResponse | null; // Product relationship (can be parent or child variant)
  partner?: PartnerResponse | null; // Partner relationship
  /**
   * Echoed by the backend on flows that were created within a
   * modify-pending / modify-confirmed request — matches the client-supplied
   * `temp_id` in the addition payload. `null`/absent for pre-existing flows.
   * The modify mutation hooks use it to locate the locally-buffered
   * (enriched) copy of the new flow so its product/unit/partner survive
   * the cache merge.
   */
  temp_id?: string | null;
}

export interface PreOrderFlowList {
  items: PreOrderFlow[];
  total: number;
}

interface EmailInfo {
  from_address: string;
}

interface SMSChatInfo {
  id: string;
  phone_number: string;
  display_name?: string | null;
  account_id: string;
}

interface EmailContact {
  id: string;
  email_address: string;
  display_name?: string;
}

// AS400 export status embedded in pre_order_metadata
export interface AS400ExportStatus {
  status?:
    | "pending"
    | "in_progress"
    | "success"
    | "failed"
    | "cancelled"
    | "retry"
    | null;
  retry_count?: number | null;
  next_retry_at?: string | null;
  last_attempt_at?: string | null;
  error_message?: string | null;
  external_reference?: string | null;
  job_id?: string | null;
}

// Drift detection between Stockline flows and the M3 (or other ERP) line state.
// Written by the backend sync poll. `ill: true` means the order is in a
// degraded state for one of several reasons:
//   - `orphan_flows`        — at least one DB flow has no matching M3 line.
//                             The heal button re-exports `orphan_flow_ids`
//                             through the modify path.
//   - `unresolved_partner`  — the M3 client/supplier code didn't map to a
//                             local partner. NOT healable from the UI —
//                             must be resolved in stkdb first.
//   - `unresolved_product`  — one or more M3 article codes didn't map to a
//                             local product. NOT healable from the UI.
//   - `unresolved_employee` — the M3 responsible code didn't map to a local
//                             employee. NOT healable from the UI.
//   - `divergent`           — content-level drift caught by the inline
//                             check on every poll/readback. Carries a list
//                             of typed findings. NOT healable from the UI
//                             — clicking heal re-runs the sync, which
//                             re-runs the check.
//   - `m3_order_locked`     — M3 rejected an export call because another
//                             user has the order open in the M3 UI (or an
//                             internal lock hasn't released yet). User-
//                             fixable: close the order in M3 and retry the
//                             export. Surfaced by the erp-integrations
//                             classifier at export-time.
// The UI surfaces all reasons as an amber "Incohérence ERP" pill on the
// confirm-export button; only `orphan_flows` enables the heal button.
export type DivergenceKind =
  | "line_count"
  | "missing_ponr"
  | "extra_ponr"
  | "itno_mismatch"
  | "qty_mismatch"
  | "status_mismatch";

export interface DivergenceDetail {
  kind: DivergenceKind;
  flow_id?: string | null;
  ponr?: number | null;
  expected?: unknown;
  actual?: unknown;
}

// Line outcome of a partial-batch export — emitted by erp-integrations
// when only a subset of the OIS100MI lines committed. The recap UI lists
// these inline so the operator can see which flow/lines need attention.
export interface PartialBatchLineOutcome {
  flow_id?: string | null;
  line_index?: number | null;
  status?: "committed" | "rejected" | string;
  error?: string | null;
}

// Per-line/per-field comment-drop signal: M3 silently swallowed a comment
// payload. The UI calls these out inline (no retry — operator must edit
// the order in M3 to re-attach the comment).
export interface CommentDropDetail {
  flow_id?: string | null;
  ponr?: number | null;
  field?: string | null;
  comment?: string | null;
}

export type SyncHealth =
  | { ill: false }
  | {
      ill: true;
      reason: "orphan_flows";
      orphan_flow_ids: string[];
      detected_at: string;
    }
  | {
      ill: true;
      reason: "unresolved_partner";
      client_code: string;
      detected_at: string;
    }
  | {
      ill: true;
      reason: "unresolved_product";
      product_codes: string[];
      detected_at: string;
    }
  | {
      ill: true;
      reason: "unresolved_employee";
      resp_code: string;
      detected_at: string;
    }
  | {
      ill: true;
      reason: "divergent";
      details: DivergenceDetail[];
      detected_at: string;
    }
  | {
      // M3 committed some OIS100MI lines but rejected others. The popover
      // lists the rejected flow ids and their per-line errors inline; no
      // retry button is offered for this reason because re-running the
      // export would re-attempt every line (the committed ones would
      // double-write). Operator handles via the modify-confirmed flow.
      ill: true;
      reason: "partial_batch";
      failed_flow_ids: string[];
      line_outcomes?: PartialBatchLineOutcome[];
      detected_at: string;
      // Carried across retries when the reason repeats — see the
      // sticky-detected_at logic in routers/pre_orders.py:export.
      history?: { reason: string; detected_at: string }[];
    }
  | {
      // M3 silently dropped one or more comment payloads (header or
      // per-line). The popover lists the dropped comments inline; no
      // retry — the operator has to re-attach in the M3 UI.
      ill: true;
      reason: "comment_drop";
      details?: CommentDropDetail[];
      detected_at: string;
    }
  | {
      // M3 rejected the modify call because another user has the order
      // open in the M3 UI (or an internal lock hasn't released yet).
      // User-fixable: close the order in M3 and retry the export.
      // Surfaced by the erp-integrations classifier at export-time and
      // also possibly by the sync poll on a delete attempt.
      ill: true;
      reason: "m3_order_locked";
      // Optional verbatim wire message from M3 for triage; the UI
      // renders a localized string regardless of this value.
      message?: string;
      // Carried verbatim from the upstream HTTP detail when present;
      // shown under the localized line as raw M3 wire context.
      error?: string;
      detected_at?: string;
    };

// Pre-order metadata structure for AI-extracted orders
// Async-confirm v1 export state stamped by V2 (apps/api).
// Written by:
//   - push-batch with intent=confirm → status: "in_progress"
//   - ConfirmConsumer readback → status: "success" | "failed"
//   - OrderSyncWorker outbound divergence → status: "failed" + last_error: "ERP_DIVERGED"
export interface ExportMetadata {
  provider?: string;
  status?: "in_progress" | "success" | "failed" | "exported" | null;
  requested_at?: string | null;
  attempt_count?: number | null;
  reference?: string | null;
  // Modern field — written by V2 from-erp service. Codes: "ERP_DIVERGED",
  // "M3_TIMEOUT", "M3_CONNECTION_REFUSED", … Renderable via the i18n
  // mapping in ExportErrorPopover. Falls back to last_error_metadata.message
  // when the worker has a free-form string to surface.
  last_error?: string | null;
  last_error_metadata?: {
    message?: string | null;
    divergence_details?: unknown[];
    [key: string]: unknown;
  } | null;
  // Legacy field — older readback paths set a free-form `error` string.
  // Kept for backward compatibility; new writes go through last_error.
  error?: string | null;
  outbound_checked_at?: string | null;
}

/** One row from a source Excel sheet, identified by its 0-based position. */
export interface SourceRow {
  index: number;
  cells: (string | number | null)[];
}

/**
 * Per-order source rows extracted from the Delanchy Excel file.
 * Persisted by Task 1 as `pre_order_metadata.source_rows` (snake_case key
 * written by Python).  `columns` holds the header labels; each `SourceRow`
 * carries 0-based `index` (original file order) + a parallel `cells` array.
 */
export interface SourceRows {
  columns: string[];
  rows: SourceRow[];
  sheet?: string;
}

export interface PreOrderMetadata {
  seen?: boolean;
  message_ids?: string[];
  // URLs of documents the AI used for order extraction. The V3 AI pre-order
  // path writes this marker camelCase (alongside `modifiedByAi` /
  // `customFields`) — match that key here, not `source_documents`, or the
  // recap never finds it. Used for auto-preview (PDF/images) and auto-play.
  sourceDocuments?: string[];
  // Per-order source rows from the Delanchy Excel (Task 1 persists snake_case
  // `source_rows`). The TS key mirrors the wire key exactly so no transform is
  // needed at read time.
  source_rows?: SourceRows;
  // AS400 export status (only present when as400Export feature flag is enabled)
  as400_export?: AS400ExportStatus;
  // Async-confirm v1 export state (V2 push-batch / ConfirmConsumer / divergence pass).
  export?: ExportMetadata;
  // Backend-written drift flag (orphan flow ids on the Stockline side).
  sync_health?: SyncHealth;
  // Additional metadata can be added here
  [key: string]: any;
}

interface WhatsAppContact {
  id: string;
  chat_jid?: string;
  name?: string;
  display_name?: string;
}

export interface PreOrder {
  id: string;
  user_id: string;
  group_id?: string | null;
  type: number;
  /** Business category (SALES=0, PURCHASES=1). Optional on the legacy shape
   * because not all upstream paths populate it yet; the offline shim now
   * carries the V2 value through. */
  category?: number;
  status: number;
  source_type: number;
  instance_id?: string;
  partner_id?: string;
  shipping_address_id?: string | null;
  location_id?: string | null;
  route_id?: string | null;
  buyer_id?: string;
  employee_id?: string | null;
  whatsapp_chat_id?: string;
  sms_chat_id?: string;
  email_id?: string;
  audio_id?: string;
  order_id?: string | null;
  pre_order_number?: string | null;
  customer_po_number?: string | null;
  comment?: string | null;
  delivery_date?: string | null;
  order_date?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null; // Soft delete timestamp
  pre_order_metadata?: PreOrderMetadata | null;
  /**
   * Denormalized visibility ACL — extra user_ids (beyond `user_id` and
   * `group_id`) who can see this row. Populated by the inbound fan-out with
   * the chat / account owners resolved through the content junctions. Server
   * uses it to route SSE / REST visibility; the UI surfaces the count on the
   * pre-order detail page so the user knows how many teammates also see it.
   */
  shared_with_user_ids?: string[];
  // Related data
  flows?: PreOrderFlow[];
  messages?: WhatsAppMessageResponse[];
  sms_messages?: SMSMessageResponse[];
  chat?: WhatsAppContact;
  whatsapp_chat?: WhatsAppContact;
  sms_chat?: SMSChatInfo;
  email_contact?: EmailContact;
  audio_file?: AudioFile;
  email?: Email;
  // Lightweight summaries of all emails linked via pre_order_emails junction.
  // Set by the recap endpoint; may be empty for sources other than EMAIL.
  emails?: PreOrderLinkedEmail[];
  /**
   * Junction-sourced linked emails with owner + Message-Id metadata. The
   * offline shim populates this from Dexie; the legacy `linked_email_ids`
   * stays for backward compatibility but new resolution code reads
   * `linked_emails` so it can collapse same-Message-Id siblings by user.
   */
  linked_emails?: PreOrderLinkedEmail[];
  // Junction-based chat ids (per-member visibility for shared dedup pre-orders).
  // Set by the recap endpoint.
  linked_whatsapp_chat_ids?: string[];
  linked_sms_chat_ids?: string[];
  /** Junction-based email ids — every email row that's linked to this PO.
   * Mirrors the chat-id arrays above for the email channel; populated by the
   * offline shim from `pre_order_emails` so the chat panel can match dedup
   * siblings to non-trigger users. */
  linked_email_ids?: string[];
  partner?: PartnerResponse;
  shipping_address?: PartnerAddress | null;
  location?: LocationResponse | null;
  buyer?: UserResponse;
  employee?: Employee | null;
}

export interface PreOrderList {
  items: PreOrder[];
  total: number;
  page: number;
  page_size: number;
}

export interface PreOrderUpdate {
  partner_id?: string | null;
  shipping_address_id?: string | null;
  location_id?: string | null;
  group_id?: string | null;
  route_id?: string | null;
  employee_id?: string | null;
  delivery_date?: string | null;
  order_date?: string | null;
  pre_order_number?: string | null;
  customer_po_number?: string | null;
  comment?: string | null;
  status?: number;
  type?: number;
  pre_order_metadata?: PreOrderMetadata | null;
}
